import { BREAKPOINTS, COLORS, SystemIcons } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { selectTranslateWithAi, setToggleTranslateWithAi } from '../store/slices/globalConfigSlice';

const Container = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 8px;
  margin: 0;
  padding: 12px 16px;
  border-radius: 4px;
  background: ${COLORS.bg_surface_primary};

  /* Components/Regular/S */
  color: var(--life-color-text-primary);
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 125% */

  > svg {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }

  ${BREAKPOINTS.MEDIUM}{
    grid-template-columns: auto 1fr auto;
  }

  /* desktop: Components/Regular/M */
  ${BREAKPOINTS.LARGE}{
    font-size: 18px;
    line-height: 24px;
  }
`;

const Text = styled.p`
  /* global p/a element rules would otherwise override the inherited spec */
  margin: 0;
  font: inherit;
  color: inherit;
`;

const linkStyle = css`
  &:link, &:visited, &:hover, &:active {
    color: ${COLORS.text_link_primary};
  }
  color: ${COLORS.text_link_primary};
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
`;

/* mobile: the link ends the paragraph */
const InlineLink = styled.a`
  ${linkStyle}
  ${BREAKPOINTS.MEDIUM}{
    display: none;
  }
`;

/* desktop/tablet: the link is its own column */
const ColumnLink = styled.a`
  ${linkStyle}
  display: none;
  ${BREAKPOINTS.MEDIUM}{
    display: inline;
    align-self: center;
    /* 64px gap to the text column (8px grid gap + 56px) */
    margin-left: 56px;
  }
`;

const TranslationBanner = () => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const translateWithAi = useSelector(selectTranslateWithAi);
  const { pathname } = useLocation();

  if (!translateWithAi) return null;

  const showOriginal = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(setToggleTranslateWithAi(false));
  };

  const linkText = t('Show original');

  return (
    <Container role="note">
      <SystemIcons.Tip size="24px" color={COLORS.text_link_primary} />
      <Text>
        {t('This content has been translated using AI and may contain inaccuracies. Please refer to the original English version for accuracy.')}
        {' '}
        <InlineLink href={pathname} onClick={showOriginal}>{linkText}</InlineLink>
      </Text>
      <ColumnLink href={pathname} onClick={showOriginal}>{linkText}</ColumnLink>
    </Container>
  );
};

export default TranslationBanner;
